/**
 * Validates a `ToolbarLayout` arriving from an imported bundle.
 *
 * `mergeToolbarLayout` (B11) already hardens the RENDER path — it dedupes, it
 * re-inserts ids that shipped after the user customized, it folds repeated
 * groups. That is the right behaviour for a layout the app itself produced.
 * An imported file is different: a duplicate there is evidence the file means
 * something the app cannot infer, and silently resolving it to primary would
 * hand the user a toolbar they did not ask for with no way to know why. So a
 * duplicate is an ERROR here, surfaced to the user, per B11's instruction.
 *
 * Unknown ids are the opposite case — a layout from a NEWER build is expected
 * and must not fail the whole import (F01-P3), so they are dropped with a
 * warning and `mergeToolbarLayout` supplies this build's defaults for them.
 */
import {
  DEFAULT_TOOLBAR_LAYOUT,
  MORE_GROUP_LABELS,
  UNHIDEABLE_TOOLBAR_ITEM_IDS,
  type ToolbarItemId,
  type ToolbarLayout,
  type ToolbarMoreGroupId,
} from '../../main-toolbar/model/toolbarItemTypes';
import type { BundleWarning } from './settingsBundleTypes';

/**
 * Anchored items B14 may not offer to hide. Declared once on the toolbar model
 * (B14 folded this constant into `toolbarItemTypes`, where the normalizer and
 * the merge read it too) and re-exported here so this module's own anti-drift
 * test against the descriptors keeps its name.
 */
export { UNHIDEABLE_TOOLBAR_ITEM_IDS };

const KNOWN_ITEM_IDS = new Set<string>([
  ...DEFAULT_TOOLBAR_LAYOUT.primary,
  ...DEFAULT_TOOLBAR_LAYOUT.more.flatMap((g) => g.items),
]);
const KNOWN_GROUP_IDS = new Set<string>(Object.keys(MORE_GROUP_LABELS));
const UNHIDEABLE = new Set<string>(UNHIDEABLE_TOOLBAR_ITEM_IDS);

export type ToolbarLayoutValidation =
  | { ok: true; layout: ToolbarLayout; warnings: BundleWarning[] }
  | { ok: false; error: string };

const isStringArray = (v: unknown): v is string[] =>
  Array.isArray(v) && v.every((e) => typeof e === 'string');

export function validateToolbarLayout(value: unknown): ToolbarLayoutValidation {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { ok: false, error: 'The toolbar layout in this file is not an object.' };
  }
  const raw = value as { primary?: unknown; more?: unknown; hidden?: unknown };

  if (!isStringArray(raw.primary)) {
    return { ok: false, error: 'The toolbar layout has no valid `primary` list of item ids.' };
  }
  // `more` is OPTIONAL. What B14 persists is `{ version, primary, hidden }`:
  // the user authors only the primary row and the More ▾ menu is derived from
  // this build's defaults, so a bundle exported by the app carries no `more`
  // at all. Requiring it would make the app's own export fail its own import.
  if (raw.more !== undefined && !Array.isArray(raw.more)) {
    return { ok: false, error: 'The toolbar layout has no valid `more` list of groups.' };
  }
  if (raw.hidden !== undefined && !isStringArray(raw.hidden)) {
    return { ok: false, error: 'The toolbar layout\'s `hidden` list must contain item ids.' };
  }

  const groups: Array<{ group: string; items: string[] }> = [];
  for (const entry of raw.more ?? []) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      return { ok: false, error: 'A More-menu group in this file is not an object.' };
    }
    const e = entry as { group?: unknown; items?: unknown };
    if (typeof e.group !== 'string' || !isStringArray(e.items)) {
      return { ok: false, error: 'A More-menu group is missing its `group` id or `items` list.' };
    }
    groups.push({ group: e.group, items: e.items });
  }

  // Duplicates are refused BEFORE unknown ids are dropped, so a duplicated
  // unknown id still reports as a duplicate rather than vanishing.
  const seen = new Set<string>();
  const inPrimary = new Set(raw.primary);
  for (const id of raw.primary) {
    if (seen.has(id)) return { ok: false, error: duplicateError(id, false) };
    seen.add(id);
  }
  for (const group of groups) {
    for (const id of group.items) {
      if (seen.has(id)) return { ok: false, error: duplicateError(id, inPrimary.has(id)) };
      seen.add(id);
    }
  }

  const warnings: BundleWarning[] = [];
  const keepItems = (ids: string[]) =>
    ids.filter((id) => {
      if (KNOWN_ITEM_IDS.has(id)) return true;
      warnings.push({
        code: 'toolbar.unknown-item',
        message: `Toolbar item "${id}" isn't part of this version — it was skipped.`,
      });
      return false;
    }) as ToolbarItemId[];

  const layout: ToolbarLayout = {
    primary: keepItems(raw.primary),
    more: groups
      .filter((g) => {
        if (KNOWN_GROUP_IDS.has(g.group)) return true;
        warnings.push({
          code: 'toolbar.unknown-group',
          message: `Toolbar group "${g.group}" isn't part of this version — it was skipped.`,
        });
        return false;
      })
      .map((g) => ({ group: g.group as ToolbarMoreGroupId, items: keepItems(g.items) })),
  };

  const hidden = (raw.hidden ?? []).filter((id) => {
    if (UNHIDEABLE.has(id)) {
      warnings.push({
        code: 'toolbar.unhideable',
        message: `"${id}" can't be hidden from the toolbar — that entry was ignored.`,
      });
      return false;
    }
    if (!KNOWN_ITEM_IDS.has(id)) {
      warnings.push({
        code: 'toolbar.unknown-item',
        message: `Hidden toolbar item "${id}" isn't part of this version — it was skipped.`,
      });
      return false;
    }
    return true;
  }) as ToolbarItemId[];
  if (hidden.length > 0) layout.hidden = hidden;

  return { ok: true, layout, warnings };
}

function duplicateError(id: string, straddlesSurfaces: boolean): string {
  return straddlesSurfaces
    ? `Toolbar item "${id}" is listed on both the toolbar and the More menu. ` +
        'Remove one of them and import again.'
    : `Toolbar item "${id}" is listed more than once in the toolbar layout. ` +
        'Remove the duplicate and import again.';
}
